import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar/AppToolbar';
import { ColumnFinder } from '../../../components/ColumnFinder/ColumnFinder';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../../components/confirm';
import { useUnsavedGuard, resetUnsavedTracking } from '../../../hooks/useUnsavedGuard';
import { runLoading } from '../../../components/loading';
import { FormTitle } from '../../../components/FormTitle';
import { ProjectNotesButton } from '../../../components/ProjectNotes';
import { formatCombo } from '../../../components/hotkeys';
import { PRE_LOGIN_FORM, type ChildTab } from './PreLoginSettingsDetailPage';
import PreLoginTestAssignment from './PreLoginTestAssignment';
import { usePreLoginSr, PreLoginContainersPanel } from './usePreLoginSr';
import { CopySamplesDialog, TargetSamplesDialog, type PickRow } from './PreLoginCopyDialogs';
import { PreLoginSitePickerDialog, type SitePickRow } from './PreLoginSitePickerDialog';
import { CellCopyMenu, CELL_MENU_CLOSED, type CellMenuState } from '../../../components/CellCopyMenu';
import { fmtLocalDate, fmtLocalDateTime } from '../../../utils/datetime';
import '../../sampling/sampleRegistration/sampleRegistration.css';
import './preLoginSettings.css';
import type {
  PreLoginSample, PreLoginSampleInput, PreLoginTest, PreLoginBottle, PreLoginParameter, PreLoginLookup,
  PreLoginSampleConfig, PreLoginSampleField, PreLoginSampleExtra, PreLoginSampleExtraInput,
} from './types';

/** Tab captions, also used in the breadcrumb. */
const TAB_LABEL: Record<ChildTab, string> = {
  samples: 'Samples', tests: 'Tests', containers: 'Containers', parameters: 'Parameters',
};

interface Props {
  cocOid: string;
  /** The record's display id, for the header + breadcrumb. */
  cocId: string | null;
  /** Which collection is showing; this page owns its own tab strip. */
  tab: ChildTab;
  setTab: (t: ChildTab) => void;
  /** Per-COC totals from the header read — the legacy buttons show these.
   *  Tests is DISTINCT TestMethod, not the per-sample row count. */
  testCount: number;
  containerCount: number;
  /** Return to the record's form. */
  onBack: () => void;
  /** Module Access Control: a role without Write on Pre-Login Settings sees the
   *  samples, tests, containers and parameters but none of the write actions
   *  (hidden, never greyed): no Add / Save / Copy / Delete, no cell editing, no
   *  Test / Cont editors, and the samples are not auto-created on open. */
  readOnly?: boolean;
}

/**
 * The SAMPLES tab of a Pre-Login Setting — the legacy "Sample" action button's
 * view (DashboardView `COCSettingsSampleRegistration` hosting ListView
 * `COCSettingsSamples_ListView_Copy_SampleRegistration`), plus the per-sample
 * TESTS and BOTTLE ALLOCATION grids the legacy screen shows beneath it
 * (`COCSettingsTest_ListView_Copy_SampleRegistration` and
 * `COCSettingsBottleAllocation_ListView_Copy_Sampleregistration`).
 *
 * <p>Each grid's columns are the legacy ListView's VISIBLE columns, in its
 * order — the legacy nodes hide most of `COCSettingsSamples`' ~190 columns with
 * `Index="-1"`.</p>
 *
 * <p>Samples are edited as a SET: New / Delete buffer locally and Save PUTs the
 * whole set in one transaction (the "Assign Tests To Item" pattern). That is
 * what makes a save atomic — a sample can never be removed while its tests stay
 * live. Tests and bottles are read-only here; they are generated from the test
 * library by the sample-registration flow, exactly as the legacy grids are
 * (`AllowNew="False"` on the bottle node).</p>
 */
export default function PreLoginSamplesPage({
  cocOid, cocId, tab, setTab, testCount, containerCount, onBack, readOnly = false,
}: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();

  const [samples, setSamples] = useState<PreLoginSample[]>([]);
  /** EVERY test / bottle / parameter row on the COC (they carry their sample's
   *  Oid) - Registration's Test, Containers and Parameters components work over
   *  all the samples at once. The per-sample views below are filtered from these. */
  const [allTests, setAllTests]     = useState<PreLoginTest[]>([]);
  const [allBottles, setAllBottles] = useState<PreLoginBottle[]>([]);
  const [allParams, setAllParams]   = useState<PreLoginParameter[]>([]);
  const [childrenLoaded, setChildrenLoaded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  /** The sample whose tests/bottles are shown below. */
  const [activeSample, setActiveSample] = useState<string | null>(null);
  /** Locally-buffered edits, keyed by sample Oid. */
  const [edits, setEdits] = useState<Record<string, Partial<PreLoginSample>>>({});
  /** True once the SET has changed — an edit, an add, or a removal. Tracked
   *  separately from the edits map because removing a row leaves no edit entry
   *  behind but still has to enable Save. */
  const [dirty, setDirty] = useState(false);
  /** The samples grid wrapper - Find Column scrolls it. */
  const gridWrapRef = useRef<HTMLDivElement>(null);
  /** Dropdown lists for the grid's combo cells (type / matrix / status). */
  const [lookups, setLookups] = useState<PreLoginLookup[]>([]);
  /** Copy Samples / Copy Test / Copy Bottles popups (server-side operations on
   *  SAVED samples — legacy commits the grid before each). */
  const [copy, setCopy] = useState<'samples' | 'test' | 'bottles' | null>(null);
  const [targets, setTargets] = useState<PickRow[]>([]);
  const [targetsLoading, setTargetsLoading] = useState(false);

  /** G5 parity: the grid's columns come from Sample Matrix Field Setup (the union over
   *  the COC's matrices). null until loaded, or when the configuration cannot be read —
   *  then the fixed legacy column set below is used, as before. */
  const [config, setConfig] = useState<PreLoginSampleConfig | null>(null);
  /** The cell that opened the "Sample Sites" popup: which sample, and which of the
   *  two site columns G5 puts the popup behind — SiteName (StationLocation, which
   *  cascades the site's address / PWS / … onto the sample) or AlternativeStation
   *  (which stores the site's name + Oid and nothing else). */
  const [sitePickFor, setSitePickFor] =
    useState<{ oid: string; field: 'StationLocation' | 'AlternativeStation' } | null>(null);
  /** The right-click menu (G5: Refresh · Copy To All Cell) on any cell. */
  const [cellMenu, setCellMenu] = useState<CellMenuState>(CELL_MENU_CLOSED);

  const loadConfig = useCallback(async () => {
    try {
      const res = await api.get(`/api/v1/pre-login-settings/${cocOid}/sample-config`);
      const cfg: PreLoginSampleConfig | undefined = res.data?.data;
      setConfig(cfg && cfg.fields.length > 0 ? cfg : null);
    } catch {
      setConfig(null);   // keep the fixed column set rather than an empty grid
    }
  }, [cocOid]);

  const loadSamples = useCallback(async () => {
    setLoading(true);
    try {
      const [res, ex] = await Promise.all([
        api.get(`/api/v1/pre-login-settings/${cocOid}/samples`),
        api.get(`/api/v1/pre-login-settings/${cocOid}/sample-extras`).catch(() => null),
      ]);
      const byOid = new Map<string, Record<string, string | null>>();
      for (const e of (ex?.data?.data ?? []) as PreLoginSampleExtra[]) {
        const k = e.sampleOid.toLowerCase();
        (byOid.get(k) ?? byOid.set(k, {}).get(k)!)[e.fieldId] = e.value;
      }
      const rows: PreLoginSample[] = ((res.data?.data ?? []) as PreLoginSample[])
        .map(r => ({ ...r, extra: byOid.get(r.oid.toLowerCase()) ?? {} }));
      setSamples(rows);
      setEdits({});
      setDirty(false);
      setActiveSample(a => (a && rows.some(r => r.oid === a) ? a : rows[0]?.oid ?? null));
    } catch {
      notify.error('Could not load the samples.');
    } finally {
      setLoading(false);
    }
  }, [cocOid, notify]);

  useEffect(() => {
    let alive = true;
    api.get('/api/v1/pre-login-settings/sample-lookups')
      .then(r => { if (alive) setLookups(r.data?.data ?? []); })
      .catch(() => { if (alive) notify.error('Could not load the sample dropdown lists.'); });
    return () => { alive = false; };
  }, [notify]);

  // First open of Samples: the server creates the "No Of Samples" samples (with the
  // header's chosen tests and their bottles) exactly once per COC and answers 0 on
  // every later call - so this is safe to fire on every mount. Failure must never
  // hide the grid, so it is swallowed into a warning and the normal load follows.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // No "N samples created" message: G5's Sample action inserts them silently
        // and just shows the grid (owner report 2026-10-21). A read-only role never
        // creates them (the API would refuse it anyway).
        if (!readOnly) await api.post(`/api/v1/pre-login-settings/${cocOid}/samples/init`);
      } catch {
        if (alive) notify.warning('Could not create the samples from No Of Samples.');
      }
      if (alive) await loadConfig();
      if (alive) await loadSamples();
      // The tests / bottles / parameters were first read while the samples were still
      // being created, so they came back empty - read them again now they exist.
      if (alive) await reloadChildren();
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cocOid]);

  /** Loads the COC's tests, bottles and parameters. Shared by the effect below
   *  and every action that changes them; returns what it loaded. */
  const reloadChildren = useCallback(async () => {
    try {
      const [t, b, p] = await Promise.all([
        api.get(`/api/v1/pre-login-settings/${cocOid}/tests`),
        api.get(`/api/v1/pre-login-settings/${cocOid}/bottles`),
        api.get(`/api/v1/pre-login-settings/${cocOid}/parameters`),
      ]);
      const tests: PreLoginTest[] = t.data?.data ?? [];
      const bottles: PreLoginBottle[] = b.data?.data ?? [];
      const params: PreLoginParameter[] = p.data?.data ?? [];
      setAllTests(tests); setAllBottles(bottles); setAllParams(params);
      setChildrenLoaded(true);
      return { tests, bottles, params };
    } catch {
      notify.error('Could not load the tests, containers and parameters.');
      return { tests: [] as PreLoginTest[], bottles: [] as PreLoginBottle[], params: [] as PreLoginParameter[] };
    }
  }, [cocOid, notify]);

  useEffect(() => { void reloadChildren(); }, [reloadChildren]);

  /** The active sample's own rows — the Test Assignment panel under the Samples
   *  grid. The Tests / Containers / Parameters TABS show the whole COC. */
  const tests   = useMemo(() => allTests.filter(r => r.sampleOid === activeSample), [allTests, activeSample]);
  const bottles = useMemo(() => allBottles.filter(r => r.sampleOid === activeSample), [allBottles, activeSample]);

  // Standing rule 15 — the sample set is buffered in memory (New / Delete /
  // cell edits are not written until "Save Samples"), so the automatic
  // edit tracker cannot see it. Register the dirty flag explicitly, or
  // navigating away would drop the whole pending set silently.
  useUnsavedGuard(dirty);

  /** The grid rows with local edits applied. */
  const rows = useMemo(
    () => samples.map(s => (edits[s.oid] ? { ...s, ...edits[s.oid] } : s)),
    [samples, edits]);

  // ── Columns — the legacy visible sets, in legacy order ────────────────────
  /** The full legacy visible set - used for Export, so nothing is lost. */
  const lookupNames = useCallback(
    (kind: string) => lookups.filter(o => o.kind === kind && o.name).map(o => o.name as string),
    [lookups]);
  const staticExportCols: AppColumn<PreLoginSample>[] = useMemo(() => { const cols: AppColumn<PreLoginSample>[] = [
    // Qty sits right next to the Cont link. G5's Qty is a plain NUMBER cell (type any whole
    // number), not Sample Registration's 1-6 list.
    { field: 'qty',                 header: 'Qty',             width: 60, align: 'right',
      edit: { type: 'number' } },
    // Legacy COCSettingsSamples_ListView_Copy_SampleRegistration: exactly these 17
    // columns in this order, batch (click-to-edit) cells whose edits are written
    // together by Save Samples. SampleID and Client are AllowEdit=False; SampleID
    // carries the legacy Count summary; SampleMatrix has AllowClear=False.
    { field: 'sampleID',            header: 'SampleID',        width: 100 },
    { field: 'sampleName',          header: 'SampleName',      width: 107, edit: { type: 'text' } },
    { field: 'sampleTypeName',      header: 'SampleType',      width: 95,
      edit: { type: 'combo', options: lookupNames('sampleType'), clearable: true } },
    { field: 'siteName',            header: 'SiteName',        width: 120, edit: { type: 'text' } },
    // G5 live grid shows the stored sampling address right after SiteName; it is
    // filled from the site on the sample, so it is display-only here.
    { field: 'samplingAddress',     header: 'SamplingAddress', width: 150 },
    { field: 'sampleMatrix',        header: 'SampleMatrix',    width: 103,
      edit: { type: 'combo', options: lookupNames('visualMatrix'), clearable: false } },
    { field: 'comment',             header: 'Comment',         width: 79,  edit: { type: 'text' } },
    { field: 'collectorPhone',      header: 'CollectorPhone',  width: 100, edit: { type: 'text' } },
    { field: 'client',              header: 'Client',          width: 212 },
    { field: 'stationLocationName', header: 'StationLocation', width: 110, edit: { type: 'text' } },
    { field: 'sampleStatusName',    header: 'SampleStatus',    width: 100,
      edit: { type: 'combo', options: lookupNames('sampleStatus'), clearable: true } },
    { field: 'remark',              header: 'Remark',          width: 100, edit: { type: 'text' } },
    { field: 'projectNumber',       header: 'ProjectNumber',   width: 110, edit: { type: 'text' } },
    { field: 'previousSample',      header: 'PreviousSample',  width: 110, edit: { type: 'text' } },
    { field: 'previousCollection',  header: 'PreviousCollection', width: 130, edit: { type: 'text' } },
    { field: 'samplingEvent',       header: 'SamplingEvent',   width: 110, edit: { type: 'text' } },
    { field: 'loginBy',             header: 'LoginBy',         width: 100 },
    { field: 'loginDateTime',       header: 'LoginDateTime',   width: 130, type: 'datetime' },
  ];
  // A read-only role gets plain cells: the click-to-edit config is dropped.
  return readOnly ? cols.map(c => (c.edit ? { ...c, edit: undefined } : c)) : cols;
  }, [lookupNames, readOnly]);

  // ── Matrix-driven columns (G5 parity) ─────────────────────────────────────
  /** Dropdown options per FieldID, both ways (Oid ↔ name). */
  const lookupMaps = useMemo(() => {
    const m = new Map<string, { byOid: Map<string, string>; byName: Map<string, string>; names: string[] }>();
    for (const l of config?.lookups ?? []) {
      if (!l.name) continue;
      const key = l.source.toLowerCase();
      const e = m.get(key) ?? m.set(key, { byOid: new Map(), byName: new Map(), names: [] }).get(key)!;
      e.byOid.set(l.oid.toLowerCase(), l.name);
      if (!e.byName.has(l.name)) { e.byName.set(l.name, l.oid); e.names.push(l.name); }
    }
    return m;
  }, [config]);

  /** The text a matrix-driven cell shows. */
  const extraText = useCallback((r: PreLoginSample, f: PreLoginSampleField): string => {
    const raw = r.extra?.[f.fieldId] ?? null;
    if (raw == null || raw === '') return '';
    switch (f.fieldType) {
      case 'dropdown': return lookupMaps.get(f.fieldId.toLowerCase())?.byOid.get(raw.toLowerCase()) ?? '';
      case 'bool':     return raw === '1' ? 'Yes' : raw === '0' ? 'No' : '';
      case 'date':     return fmtLocalDate(raw);
      case 'datetime': return fmtLocalDateTime(raw);
      default:         return raw;
    }
  }, [lookupMaps]);

  /** The columns Sample Matrix Field Setup gives this COC: frozen ones first (pinned left),
   *  then by SortOrder; caption / width from the setup. The fields the fixed sample row
   *  already carries use their own editors; every other field reads / writes `extra`. */
  const dynamicExportCols: AppColumn<PreLoginSample>[] = useMemo(() => {
    if (!config) return [];
    const fields = [...config.fields].sort((a, b) =>
      (Number(b.freeze) - Number(a.freeze)) || (a.sortOrder - b.sortOrder) || a.fieldId.localeCompare(b.fieldId));
    const edit = <T,>(e: T): T | undefined => (readOnly ? undefined : e);
    const text = (field: keyof PreLoginSample, f: PreLoginSampleField): AppColumn<PreLoginSample> =>
      ({ field: String(field), header: f.caption ?? f.fieldId, width: f.width, edit: edit({ type: 'text' as const }) });

    return fields.map((f): AppColumn<PreLoginSample> => {
      const header = f.caption ?? f.fieldId;
      switch (f.fieldId) {
        // — the fixed sample row's own fields —
        case 'SampleID':           return { field: 'sampleID', header, width: f.width };
        case 'ClientSampleID':     return text('sampleName', f);
        case 'Remark':             return text('remark', f);
        case 'CollectorPhone':     return text('collectorPhone', f);
        case 'ProjectNumber':      return text('projectNumber', f);
        case 'PreviousSample':     return text('previousSample', f);
        case 'PreviousCollection': return text('previousCollection', f);
        case 'SamplingEvent':      return text('samplingEvent', f);
        case 'Qty':
          return { field: 'qty', header, width: f.width, align: 'right' as const,
                   edit: edit({ type: 'number' as const }) };
        case 'VisualMatrix':
          return { field: 'sampleMatrix', header, width: f.width,
                   edit: edit({ type: 'combo' as const, options: lookupNames('visualMatrix'), clearable: false }) };
        case 'SampleType':
          return { field: 'sampleTypeName', header, width: f.width,
                   edit: edit({ type: 'combo' as const, options: lookupNames('sampleType'), clearable: true }) };
        case 'Collector':
          return { field: 'collectorName', header, width: f.width,
                   edit: edit({ type: 'combo' as const,
                                options: lookupMaps.get('collector')?.names ?? [], clearable: true }) };
        case 'SubOut':
          return { field: 'subOut', header, width: f.width, value: r => (r.subOut ? 'Yes' : 'No'),
                   edit: edit({ type: 'combo' as const, options: ['Yes', 'No'], clearable: false }) };
        default: break;
      }

      // — everything else is a value in `extra`, edited by its real type —
      const key = `x:${f.fieldId}`;
      const base: AppColumn<PreLoginSample> = {
        field: key, header, width: f.width, value: r => extraText(r, f),
      };
      // A SITE cell: G5 puts the same "Sample Sites" popup behind BOTH site columns,
      // never a typed value (owner report 2026-11-03 — AlternativeStation was a plain
      // text box here). StationLocation is a uniqueidentifier so it arrives as a
      // 'dropdown'; AlternativeStation is text, so it has to be caught before the
      // type switch below or it renders as a free-text editor again.
      if (!readOnly && !f.readOnly && (f.fieldId === 'StationLocation' || f.fieldId === 'AlternativeStation')) {
        const which = f.fieldId as 'StationLocation' | 'AlternativeStation';
        return {
          ...base,
          render: r => (
            // Plain cell text like every other cell (G5 shows no link); the click opens the popup.
            <span role="button" tabIndex={0}
                  style={{ display: 'block', minHeight: 18, cursor: 'pointer' }}
                  title="Select a site…"
                  onClick={e => { e.stopPropagation(); setSitePickFor({ oid: r.oid, field: which }); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault(); setSitePickFor({ oid: r.oid, field: which });
                    }
                  }}>
              {extraText(r, f) || ' '}
            </span>
          ),
        };
      }
      // Read-only fields keep their real type (so the value is formatted / resolved)
      // but get no editor; 'ro' means the column does not exist at all.
      if (readOnly || f.readOnly || f.fieldType === 'ro') return base;

      switch (f.fieldType) {
        case 'text':   return { ...base, edit: { type: 'text' } };
        case 'number': return { ...base, edit: { type: 'number' } };
        case 'time':   return { ...base, value: r => r.extra?.[f.fieldId] ?? '', edit: { type: 'time' } };
        // The date editors take / return the wire value, so the cell value is the raw ISO.
        case 'date':     return { ...base, value: r => r.extra?.[f.fieldId] ?? '', edit: { type: 'date' } };
        case 'datetime': return { ...base, value: r => r.extra?.[f.fieldId] ?? '', edit: { type: 'datetime' } };
        case 'bool':   return { ...base, edit: { type: 'combo', options: ['Yes', 'No'], clearable: true } };
        case 'dropdown':
          // (both site columns are handled above - G5 picks a site in a popup)
          return { ...base, edit: { type: 'combo', options: lookupMaps.get(f.fieldId.toLowerCase())?.names ?? [], clearable: true } };
        default: return base;
      }
    });
  }, [config, readOnly, lookupNames, lookupMaps, extraText]);

  /** The popup's rows: the client's sites with the PWSID / WaterType Oids shown as names. */
  const sitePickRows: SitePickRow[] = useMemo(() => (config?.sites ?? []).map(s => ({
    oid:             s.oid,
    siteName:        s.name ?? '',
    address:         s.address ?? '',
    keyMap:          s.keyMap ?? '',
    pwsId:           s.pwsId ? (lookupMaps.get('pwsid')?.byOid.get(s.pwsId.toLowerCase()) ?? '') : '',
    samplePointId:   s.samplePointId ?? '',
    samplePointType: s.samplePointType ?? '',
    waterType:       s.waterType ? (lookupMaps.get('watertype')?.byOid.get(s.waterType.toLowerCase()) ?? '') : '',
  })), [config, lookupMaps]);

  const sampleExportCols = config ? dynamicExportCols : staticExportCols;
  /** Leading data columns pinned left: the Test / Cont links plus the setup's Freeze columns. */
  const stickyCount = config
    ? (readOnly ? 0 : 2) + config.fields.filter(f => f.freeze).length
    : 4;

  /** Registration's Test / Containers / Parameters windows (see usePreLoginSr). */
  const srRef = useRef<ReturnType<typeof usePreLoginSr> | null>(null);
  const sr = {
    openTest: (oid: string) => srRef.current?.openTest(oid),
    openCont: (oid: string, key?: string | null) => srRef.current?.openCont(oid, key),
  };

  /** The grid shows the full legacy visible set and SCROLLS horizontally (owner
   *  report 2026-09-29: a fixed-width layout took the scrollbar away and the
   *  columns could not be reached). */
  const sampleCols: AppColumn<PreLoginSample>[] = useMemo(() => [
    // Test / Cont row-link columns, ahead of Qty and SampleID — Sample
    // Registration parity. "Test" opens the Test Assignment Edit window for that
    // sample, "Cont" its Containers window. Both open EDITORS, so a read-only role
    // does not get them (the Tests / Containers tabs show the same data to read).
    ...(readOnly ? [] : [{
      field: '__test', header: 'Test', width: 56, align: 'center' as const,
      sortable: false, filterable: false, noExport: true,
      // The legacy Count summary sits at the grid's left corner, under the first
      // column (owner request 2026-10-20), not under SampleID.
      summary: (rs: PreLoginSample[]) => `Count=${rs.length}`,
      render: (s: PreLoginSample) => (
        <button type="button" className="sr-gridlink" title={`Tests for ${s.sampleID ?? ''}`}
          onClick={e => { e.stopPropagation(); void sr.openTest(s.oid); }}>
          Test
        </button>
      ),
    },
    {
      field: '__cont', header: 'Cont', width: 56, align: 'center' as const,
      sortable: false, filterable: false, noExport: true,
      render: (s: PreLoginSample) => (
        <button type="button" className="sr-gridlink" title={`Containers for ${s.sampleID ?? ''}`}
          onClick={e => { e.stopPropagation(); sr.openCont(s.oid); }}>
          Cont
        </button>
      ),
    }]),
    // Right-click ANY data cell → Refresh · Copy To All Cell (G5's grid context menu).
    ...sampleExportCols.map(c => ({
      ...c,
      onCellContextMenu: (e: React.MouseEvent<HTMLTableCellElement>, row: PreLoginSample) => {
        e.preventDefault();
        setCellMenu({ visible: true, x: e.clientX, y: e.clientY, rowId: row.oid, field: c.field });
      },
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [sampleExportCols, readOnly]);

  const testCols: AppColumn<PreLoginTest>[] = useMemo(() => [
    { field: 'sampleID',      header: 'SampleID',      width: 120 },
    { field: 'matrix',        header: 'Matrix',        width: 110 },
    { field: 'test',          header: 'Test',          width: 200 },
    { field: 'testGroupName', header: 'TestGroupName', width: 150 },
    { field: 'method',        header: 'Method',        width: 130 },
    { field: 'components',    header: 'Components',    width: 140 },
    { field: 'subOut',        header: 'SubOut',        width: 80 },
    { field: 'testParams',    header: 'Parameter',     width: 110 },
  ], []);

  /** Containers as a plain grid — only used for a read-only role (see the Containers tab). */
  const bottleCols: AppColumn<PreLoginBottle>[] = useMemo(() => [
    { field: 'sampleID',         header: 'SampleID',    width: 120 },
    { field: 'test',             header: 'Test',        width: 200 },
    { field: 'bottleID',         header: 'BottleID',    width: 90 },
    { field: 'method',           header: 'Method',      width: 130 },
    { field: 'containerName',    header: 'Container',   width: 150 },
    { field: 'preservativeName', header: 'Preservative', width: 150 },
    { field: 'storageName',      header: 'Storage',     width: 150 },
  ], []);

  /** The Parameters tab — the Tests collection UNCOLLAPSED. The Tests grid
   *  above shows DISTINCT TestMethod (legacy's Tests(7) over 13 rows); these
   *  are those rows.
   *
   *  The reader still returns MDL / RptLimit / Result / QC Type / Units / SubOut
   *  and the `hasOverride` flag that distinguishes a COC's own limit from the
   *  inherited library default. They are no longer COLUMNS (owner request
   *  2026-09-28) but the API is left intact: nothing else costs anything to
   *  keep, and stripping the SP would make restoring a column a DB change. */
  const paramCols: AppColumn<PreLoginParameter>[] = useMemo(() => [
    // Owner request 2026-09-28: the analytical columns (QC Type · Units · MDL ·
    // RptLimit · Result · SubOut) are gone and COMPONENT sits before Parameter.
    // That reads as the identity chain the tab is actually for — which sample,
    // which test, by which method, of which component, which parameter — rather
    // than the result-bearing fields, which belong to a run and are empty on a
    // template anyway (every live row on COC0433 had a blank Result).
    { field: 'sampleID',  header: 'SampleID',  width: 120 },
    { field: 'test',      header: 'Test',      width: 180 },
    { field: 'method',    header: 'Method',    width: 120 },
    { field: 'component', header: 'Component', width: 140 },
    { field: 'parameter', header: 'Parameter', width: 190 },
  ], []);


  // ── Actions ───────────────────────────────────────────────────────────────

  /** Builds the full set PUT — every row, edits applied, new rows with a null
   *  Oid. The server allocates SampleNo for the new ones race-safely. */
  const toInputs = useCallback((list: PreLoginSample[]): PreLoginSampleInput[] =>
    list.map(s => ({
      oid:                  s.oid.startsWith('new:') ? null : s.oid,
      sampleName:           s.sampleName,
      sampleTypeOid:        s.sampleTypeOid,
      siteName:             s.siteName,
      visualMatrixOid:      s.visualMatrixOid,
      comment:              s.comment,
      collectorOid:         s.collectorOid,
      collectorPhone:       s.collectorPhone,
      stationLocationName:  s.stationLocationName,
      sampleStatusOid:      s.sampleStatusOid,
      remark:               s.remark,
      projectNumber:        s.projectNumber,
      previousSample:       s.previousSample,
      previousCollection:   s.previousCollection,
      samplingEvent:        s.samplingEvent,
      qty:                  s.qty,
      containers:           s.containers,
      bottleQty:            s.bottleQty,
      preservetives:        s.preservetives,
      storageOid:           s.storageOid,
      preserveConditionOid: s.preserveConditionOid,
      qcTypeOid:            s.qcTypeOid,
      latitude:             s.latitude,
      longitude:            s.longitude,
      depth:                s.depth,
      sampleSource:         s.sampleSource,
      subOut:               s.subOut,
      hold:                 s.hold,
      sortOrder:            s.sortOrder,
    })), []);

  /** A grid cell committed - buffered; nothing is written until Save Samples. */
  /** Buffers an edit of one sample (merged into whatever is already pending for it). */
  const patchSample = useCallback((row: PreLoginSample, patch: Partial<PreLoginSample>) => {
    setEdits(e => ({ ...e, [row.oid]: { ...e[row.oid], ...patch } }));
    setDirty(true);
  }, []);

  /** A matrix-driven cell committed: validate it by its field type, store it in `extra`. */
  const commitExtra = useCallback((row: PreLoginSample, fieldId: string, value: string) => {
    const f = config?.fields.find(x => x.fieldId === fieldId);
    if (!f) return;
    let stored: string | null;
    switch (f.fieldType) {
      case 'dropdown': stored = value ? (lookupMaps.get(fieldId.toLowerCase())?.byName.get(value) ?? null) : null; break;
      case 'bool':     stored = value === 'Yes' ? '1' : value === 'No' ? '0' : null; break;
      case 'number':
        if (value.trim() !== '' && !Number.isFinite(Number(value))) { notify.warning(`${f.caption ?? fieldId} must be a number.`); return; }
        stored = value.trim() === '' ? null : value.trim();
        break;
      default: stored = value.trim() === '' ? null : value;
    }
    patchSample(row, { extra: { ...(row.extra ?? {}), [fieldId]: stored } });
  }, [config, lookupMaps, notify, patchSample]);

  /** A site chosen in the "Sample Sites" popup. Like G5 (COCSettingsController ObjectChanged
   *  "SiteName") it copies the site's address, key map, system type, PWS … onto the sample — for
   *  the fields this matrix actually shows. Qty / collector / phone only when the site has one. */
  const pickSite = useCallback((siteOid: string) => {
    const row = rows.find(r => r.oid === sitePickFor?.oid);
    const site = config?.sites.find(s => s.oid === siteOid);
    const field = sitePickFor?.field;
    setSitePickFor(null);
    if (!row || !site || !config) return;

    // ALTERNATIVE STATION is just a second site on the sample — it names a station
    // the sample may be taken from instead, it does not re-address the sample. So it
    // stores the site's NAME and Oid and cascades nothing, exactly as Sample
    // Registration's own AlternativeStation picker does (SampleLoginView), and the
    // pair is what the Pre-Login → job import carries onto SampleLogIn.
    if (field === 'AlternativeStation') {
      patchSample(row, { extra: {
        ...(row.extra ?? {}),
        AlternativeStation:    site.name ?? null,
        AlternativeStationOid: site.oid,
      } });
      return;
    }

    const shown = new Set(config.fields.map(x => x.fieldId));
    const extra: Record<string, string | null> = { ...(row.extra ?? {}), StationLocation: site.oid };
    const copy: [string, string | null][] = [
      ['SamplingAddress', site.samplingAddress], ['KeyMap', site.keyMap], ['SamplePointID', site.samplePointId],
      ['SamplePointType', site.samplePointType], ['Address', site.address], ['SystemType', site.systemType],
      ['PWSID', site.pwsId], ['PWSSystemName', site.pwsSystemName], ['RejectionCriteria', site.rejectionCriteria],
      ['RepeatLocation', site.repeatLocation], ['ParentSampleID', site.parentSampleId],
      ['Description', site.description], ['WaterType', site.waterType],
    ];
    for (const [id, v] of copy) if (shown.has(id)) extra[id] = v?.toString().trim() ? v : null;

    const patch: Partial<PreLoginSample> = { extra, stationLocationName: site.name };
    if (site.qty && site.qty > 0) patch.qty = site.qty;
    if (site.collector) {
      patch.collectorOid = site.collector;
      patch.collectorName = lookupMaps.get('collector')?.byOid.get(site.collector.toLowerCase()) ?? row.collectorName;
    }
    if (site.collectorPhone) patch.collectorPhone = site.collectorPhone;
    patchSample(row, patch);
  }, [rows, sitePickFor, config, lookupMaps, patchSample]);

  /** G5's "Copy To All Cell" (COCSettingsViewController.cs :2559-2640): the clicked cell's value
   *  goes to every TICKED row. For a site it takes the site's related fields along; for Sample
   *  Type / Rejection Criteria only rows of the same sample matrix are touched. */
  const copyCellToSelected = useCallback(() => {
    const field = cellMenu.field;
    const rowId = cellMenu.rowId;
    setCellMenu(CELL_MENU_CLOSED);
    if (!field) return;
    const src = rows.find(r => r.oid === rowId);
    if (!src) return;
    if (selected.size === 0) { notify.warning('Please select at least one row.'); return; }
    const col = sampleExportCols.find(c => c.field === field);
    // The two site columns are popup cells, so they carry no `edit` config — they are
    // still copyable (they were, as text, before the popup went on AlternativeStation).
    if (readOnly || !col || !(col.edit || field === 'x:StationLocation' || field === 'x:AlternativeStation')) {
      notify.warning('This column cannot be copied.');
      return;
    }

    const sameMatrixOnly = field === 'sampleTypeName' || field === 'x:RejectionCriteria';
    const shown = new Set(config?.fields.map(f => f.fieldId) ?? []);
    const SITE_FIELDS = ['SamplingAddress', 'Address', 'PWSID', 'KeyMap', 'SamplePointID', 'SamplePointType',
      'SystemType', 'PWSSystemName', 'RejectionCriteria', 'WaterType', 'ParentSampleID', 'ParentSampleDate'];

    for (const t of rows) {
      if (!selected.has(t.oid)) continue;
      if (sameMatrixOnly && t.visualMatrixOid !== src.visualMatrixOid) continue;
      let patch: Partial<PreLoginSample>;
      if (field.startsWith('x:')) {
        const id = field.slice(2);
        const extra = { ...(t.extra ?? {}), [id]: src.extra?.[id] ?? null };
        if (id === 'StationLocation')
          for (const k of SITE_FIELDS) if (shown.has(k)) extra[k] = src.extra?.[k] ?? null;
        // The alternative station is a name + Oid pair; copying the name alone would
        // leave the target row pointing at the site it had before.
        if (id === 'AlternativeStation') extra.AlternativeStationOid = src.extra?.AlternativeStationOid ?? null;
        patch = id === 'StationLocation' ? { extra, stationLocationName: src.stationLocationName } : { extra };
      } else {
        switch (field) {
          case 'sampleTypeName': patch = { sampleTypeOid: src.sampleTypeOid, sampleTypeName: src.sampleTypeName }; break;
          case 'sampleMatrix':   patch = { visualMatrixOid: src.visualMatrixOid, sampleMatrix: src.sampleMatrix }; break;
          case 'collectorName':  patch = { collectorOid: src.collectorOid, collectorName: src.collectorName }; break;
          default: patch = { [field]: (src as unknown as Record<string, unknown>)[field] } as Partial<PreLoginSample>;
        }
      }
      patchSample(t, patch);
    }
  }, [cellMenu, rows, selected, config, sampleExportCols, readOnly, notify, patchSample]);

  const onCellCommit = useCallback((row: PreLoginSample, col: AppColumn<PreLoginSample>, value: string) => {
    const f = String(col.field);
    if (f.startsWith('x:')) { commitExtra(row, f.slice(2), value); return; }
    const idOf = (kind: string) => lookups.find(o => o.kind === kind && o.name === value)?.id ?? null;
    let patch: Partial<PreLoginSample>;
    switch (f) {
      case 'collectorName': {
        const id = value ? (lookupMaps.get('collector')?.byName.get(value) ?? null) : null;
        patch = { collectorOid: id, collectorName: id ? value : null }; break;
      }
      case 'subOut': patch = { subOut: value === 'Yes' }; break;
      case 'sampleTypeName':   { const id = idOf('sampleType');   patch = { sampleTypeOid: id,   sampleTypeName: id ? value : null }; break; }
      case 'sampleMatrix':     { const id = idOf('visualMatrix'); patch = { visualMatrixOid: id, sampleMatrix: id ? value : null }; break; }
      case 'sampleStatusName': { const id = idOf('sampleStatus'); patch = { sampleStatusOid: id, sampleStatusName: id ? value : null }; break; }
      case 'qty': { const n = parseInt(value, 10); patch = { qty: Number.isFinite(n) && n > 0 ? n : 1 }; break; }
      default: patch = { [f]: value.trim() === '' ? null : value } as Partial<PreLoginSample>;
    }
    setEdits(e => ({ ...e, [row.oid]: { ...e[row.oid], ...patch } }));
    setDirty(true);
  }, [lookups, lookupMaps, commitExtra]);

  /** The matrix-driven values that CHANGED against what was loaded, per sample — sent with
   *  the set so they are written in the same transaction (and audited one by one). */
  const buildExtras = useCallback((list: PreLoginSample[]): PreLoginSampleExtraInput[] => {
    const out: PreLoginSampleExtraInput[] = [];
    for (const r of list) {
      if (r.oid.startsWith('new:')) continue;
      const orig = samples.find(s => s.oid === r.oid)?.extra ?? {};
      const cur = r.extra ?? {};
      const f: Record<string, string | null> = {};
      for (const k of Object.keys(cur)) if ((cur[k] ?? null) !== (orig[k] ?? null)) f[k] = cur[k] ?? null;
      if (Object.keys(f).length > 0) out.push({ k: r.oid, f });
    }
    return out;
  }, [samples]);

  /** Writes the whole set plus the changed matrix-driven values, in one transaction. */
  const putSet = useCallback((list: PreLoginSample[], extraRows: PreLoginSampleInput[] = []) =>
    api.put(`/api/v1/pre-login-settings/${cocOid}/samples/fields`,
      { rows: [...toInputs(list), ...extraRows], extras: buildExtras(list) }),
  [cocOid, toInputs, buildExtras]);

  /** Delete - G5's DeleteAction_Execute (COCSettingsViewController.cs :111): the ticked
   *  samples go at ONCE (with their tests and bottle allocations), "Deleted successfully."
   *  is shown, and the survivors are renumbered 1..n by the save SP so the SampleIDs
   *  close up (delete -002 and -003 becomes -002). Any grid edits still pending are
   *  written by the same call, as with Add Sample. */
  const removeSelected = useCallback(async () => {
    if (selected.size === 0) return;
    await runDelete({
      formName: PRE_LOGIN_FORM,
      message: `Delete ${selected.size} sample${selected.size === 1 ? '' : 's'}? `
             + 'Their tests and bottle allocations are deleted too.',
      delete: async () => {
        const keep = rows.filter(r => !selected.has(r.oid));
        await putSet(keep);
      },
      onDeleted: () => { setSelected(new Set()); resetUnsavedTracking(); void loadSamples().then(() => reloadChildren()); },
    });
  }, [selected, rows, runDelete, putSet, loadSamples, reloadChildren]);

  const save = useCallback(async () => {
    await runSave({
      formName: PRE_LOGIN_FORM,
      auditAction: 'edit',
      save: async () => {
        await putSet(rows);
      },
      onSaved: () => void loadSamples(),
    });
  }, [rows, runSave, putSet, loadSamples]);

  // ── Copy Samples / Copy Test / bottle operations ─────────────────────────
  // Each runs on the server against SAVED rows (legacy commits the grid first),
  // so a pending buffered edit blocks them rather than being silently lost.

  const apiMsg = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message ?? fallback;

  /** The single checked, saved sample a copy runs from — or null after telling
   *  the user why not (legacy messages verbatim). */
  const copySource = useCallback((): PreLoginSample | null => {
    if (selected.size === 0) { notify.warning('Please select an item to proceed further.'); return null; }
    if (selected.size > 1) { notify.warning('Select only one check box.'); return null; }
    if (dirty) { notify.warning('Save the samples first, then copy.'); return null; }
    const row = rows.find(r => selected.has(r.oid));
    if (!row || row.oid.startsWith('new:')) { notify.warning('Save the samples first, then copy.'); return null; }
    return row;
  }, [selected, dirty, rows, notify]);

  const afterServerOp = useCallback(async () => {
    await loadSamples();
    await reloadChildren();
  }, [loadSamples, reloadChildren]);

  /** Qty edits made in the Containers window / tab go through the samples PUT. */
  const persistQty = useCallback(async (qty: Record<string, number>) => {
    const next = samples.map(s => (qty[s.oid] != null ? { ...s, qty: qty[s.oid]! } : s));
    await api.put(`/api/v1/pre-login-settings/${cocOid}/samples`, toInputs(next));
    await loadSamples();
  }, [samples, cocOid, toInputs, loadSamples]);

  /** Add Sample - G5's SimpleAction1_Execute (COCSettingsViewController.cs :5698):
   *  creates ONE new sample on the COC at once (no confirm, no message), numbered
   *  after the existing ones, with the Sample Matrix pre-filled when the COC has
   *  exactly one. It carries no tests - they are assigned afterwards. The new row
   *  goes through the same whole-set PUT as Save Samples (which allocates SampleNo
   *  race-safely and audits the change), so any cell edits still pending in the grid
   *  are written with it. */
  const addSample = async () => {
    let visualMatrixOid: string | null = null;
    try {
      const h = await api.get(`/api/v1/pre-login-settings/${cocOid}`);
      const ids = String(h.data?.data?.sampleMatrixIds ?? '').split(';').map((x: string) => x.trim()).filter(Boolean);
      if (ids.length === 1) visualMatrixOid = ids[0]!;
    } catch { /* no header read - the sample is added without a matrix, as G5 does for several */ }
    const blank: PreLoginSampleInput = {
      oid: null, sampleName: null, sampleTypeOid: null, siteName: null, visualMatrixOid,
      comment: null, collectorOid: null, collectorPhone: null, stationLocationName: null,
      sampleStatusOid: null, remark: null, projectNumber: null, previousSample: null,
      previousCollection: null, samplingEvent: null, qty: 1, containers: null, bottleQty: null,
      preservetives: null, storageOid: null, preserveConditionOid: null, qcTypeOid: null,
      latitude: null, longitude: null, depth: null, sampleSource: null, subOut: false,
      hold: false, sortOrder: null,
    };
    try {
      await runLoading(
        putSet(rows, [blank]),
        { blocking: true, label: 'Saving…' });
      resetUnsavedTracking();
      await afterServerOp();
    } catch (e) { notify.error(apiMsg(e, 'Could not add the sample.')); }
  };

  const openCopySamples = () => { if (copySource()) setCopy('samples'); };

  const openCopyTest = async () => {
    const src = copySource();
    if (!src) return;
    setCopy('test');
    setTargetsLoading(true);
    try {
      const res = await api.get(`/api/v1/pre-login-settings/samples/${src.oid}/copy-test-targets`);
      setTargets((res.data?.data ?? []).map((t: { oid: string; sampleID: string | null; sampleName: string | null; sampleMatrix: string | null }) => t));
    } catch { notify.error('Could not load the target samples.'); }
    finally { setTargetsLoading(false); }
  };

  /** Copy Samples OK - G5's CopySamples_Accepting (COCSettingsSamplesViewController.cs :531):
   *  no "Do you want to Save?" confirmation; it copies the sample N times and shows the
   *  green "Saved successfully." toast at the top. The server audits the change. */
  const runCopySamples = async (count: number) => {
    const src = copySource();
    if (!src) return;
    try {
      await runLoading(
        api.post(`/api/v1/pre-login-settings/samples/${src.oid}/copy`, { count }),
        { blocking: true, label: 'Saving…' });
      resetUnsavedTracking();
      setCopy(null);
      setSelected(new Set());
      notify.success('Saved successfully.');
      await afterServerOp();
    } catch (e) { notify.error(apiMsg(e, 'Could not copy the samples.')); }
  };

  const runCopyTest = (oids: string[]) => {
    const src = copySource();
    if (!src) return;
    void runSave({
      formName: PRE_LOGIN_FORM, auditAction: 'edit',
      successMessage: oids.length === 1
        ? 'The tests copied are applied to the selected sample successfully.'
        : 'The tests copied are applied to the selected samples successfully.',
      save: async () => {
        try { await api.post(`/api/v1/pre-login-settings/samples/${src.oid}/copy-test`, { targets: oids }); }
        catch (e) { notify.error(apiMsg(e, 'Could not copy the tests.')); throw e; }
      },
      onSaved: () => { setCopy(null); void loadSamples().then(() => reloadChildren()); },
    });
  };

  const activeSampleId = useMemo(
    () => rows.find(r => r.oid === activeSample)?.sampleID ?? null, [rows, activeSample]);

  // Registration's own Test Assignment window, Containers window / tab and
  // parameter checklist, fed from this COC (see usePreLoginSr / preLoginSr).
  const srHook = usePreLoginSr({
    cocOid, cocId, samples: rows, tests: allTests, bottles: allBottles, params: allParams,
    dirty, setActiveSample, onChanged: reloadChildren, persistQty,
  });
  srRef.current = srHook;

  return (
    <div className="bl-page sr-detail pl-detail">
      {/* ── Page header: breadcrumb back to the record, then the three
             collection tabs with their counts. Mirrors Sample Registration,
             whose Sample Login / Tests / Containers steps are likewise a
             SEPARATE page that replaces the registration form. ── */}
      <div className="bl-top">
        <div className="sr-crumb">
          <a onClick={onBack}>Settings</a>
          <span className="sr-crumb-sep">›</span>
          <a onClick={onBack}>Pre-Login Settings</a>
          <span className="sr-crumb-sep">›</span>
          <a onClick={onBack}>{cocId ?? ''}</a>
          <span className="sr-crumb-sep">›</span>
          <b>{TAB_LABEL[tab]}</b>
        </div>

        <div className="sr-savebar">
          <h2 style={{ margin: 0, fontSize: 16, color: 'var(--ai-ink)' }}>
            {cocId ? `Pre-Login Settings – ${cocId}` : 'Pre-Login Settings'}
            <span style={{ color: 'var(--ai-muted)', fontWeight: 400 }}> · {TAB_LABEL[tab]}</span>
          </h2>
          {/* View-only audit chip on the TITLE row — v4 §6/§8 ruling, never in
              an action toolbar. §8 wants it on every screen of the BO. */}
          <FormTitle pageId="set-prelogin" auditFormName={PRE_LOGIN_FORM} auditSourceId={cocOid} />
          {/* …and Project Notes beside it: §8 pairs the two components and asks
              for BOTH on every screen of the BO. This page is still that one
              COC, so the notes are the same record's — the shared button over
              the form's existing note routes (rule 8). */}
          <ProjectNotesButton
            recordId={cocOid}
            baseUrl={cocOid ? `/api/v1/pre-login-settings/${cocOid}/notes` : null}
            recordLabel={cocId ?? ''}
            businessObject={PRE_LOGIN_FORM}
          />
        </div>

        {/* The shared in-form TAB STRIP (`.sr-tabbar` / `.sr-tb`) — the SAME
            control Sample Registration's Sample Login tabs use, not the
            `.sa-filter-btn` pills this carried until 2026-09-28. These switch
            between views of one record, which is what a tab means; the orange
            pills read as filter chips narrowing a single list. */}
        <div className="sr-tabbar" style={{ marginTop: 6, display: 'flex', alignItems: 'center' }} role="tablist">
          {([
            ['samples',    `Samples (${samples.length})`],
            ['tests',      `Tests (${childrenLoaded ? new Set(allTests.map(t => t.testMethodOid ?? t.test)).size : testCount})`],
            ['containers', `Containers (${childrenLoaded ? allBottles.length : containerCount})`],
            // Parameters sits NEXT TO Containers, matching Sample Registration's
            // own sample tabs (Sample Login · Test View · Containers · Parameters).
            // Per-COC like Tests / Containers beside it — the tab lists every
            // sample, so a per-sample count would not describe what it opens.
            ['parameters', `Parameters (${childrenLoaded ? allParams.length : 0})`],
          ] as const).map(([id, label]) => (
            <button key={id} role="tab" aria-selected={tab === id}
                    className={`sr-tb${tab === id ? ' active' : ''}`}
                    onClick={() => setTab(id)}>{label}</button>
          ))}
          {/* Back sits on the tab-strip row, right-aligned (owner request 2026-10-09). */}
          <button className="ca-btn" style={{ marginLeft: 'auto' }} onClick={onBack}
                  title={`Back to the record (${formatCombo('esc')})`}>
            <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-blue)' }} /> Back
          </button>
        </div>
      </div>

      <div className="bl-grid-area" style={{ overflow: tab === 'samples' ? 'hidden' : 'auto' }}>
        {tab === 'samples' && (
          // Sample Registration's Sample Login layout (`.sr-sl-layout`): TWO bordered
          // panels sharing the page height 50 / 50, each grid scrolling inside its own
          // panel - so a short sample list no longer leaves a half-empty card and the
          // two grids always line up. Test Assignment is the second panel.
          <div className="sr-sl-layout">
            <div className="sr-sl-panel sr-sl-samples">
              <h3 className="sr-sec-h">
                Samples{cocId ? ' - ' : ''}
                <span style={{ color: 'var(--brand-accent-hover, var(--ai-orange-hover))' }}>{cocId ?? ''}</span>
                <span className="sr-sec-note">
                  {readOnly ? 'view only' : <>click a cell to edit · Save Samples writes every change together{dirty ? ' · unsaved changes' : ''}</>}
                </span>
              </h3>
              <AppToolbar
                showMeta={false}
                onDelete={readOnly ? undefined : () => void removeSelected()}
                deleteDisabled={selected.size === 0}
                onRefresh={() => void loadSamples()}
                refreshing={loading}
                left={readOnly ? undefined : <>
                  <button className="ca-btn ca-btn-solid" disabled={!dirty}
                          onClick={() => void save()}>
                    <i className="ti ti-device-floppy" /> Save Samples
                  </button>
                  <button className="ca-btn" onClick={() => void addSample()}>
                    <i className="ti ti-plus" style={{ color: 'var(--ai-green)' }} /> Add Sample
                  </button>
                  <button className="ca-btn" onClick={openCopySamples}>
                    <i className="ti ti-copy" /> Copy Samples
                  </button>
                  <button className="ca-btn" onClick={() => void openCopyTest()}>
                    <i className="ti ti-copy" /> Copy Test
                  </button>
                </>}
                exportData={{
                  filename: 'Pre-Login Samples',
                  headers: sampleExportCols.map(c => String(c.headerText ?? c.header)),
                  rows: rows.map(r => sampleExportCols.map(c => String((c.value ? c.value(r) : (r as never)[c.field]) ?? ''))),
                }}
              />
              {/* Find Column - the grid is 17 columns wide (shared with Sample Login). */}
              <ColumnFinder columns={sampleExportCols.map(c => String(c.headerText ?? c.header))} gridRef={gridWrapRef} />
              <div ref={gridWrapRef} className="sr-sl-grid-wrap">
                <AppDataGrid<PreLoginSample>
                  rows={rows}
                  columns={sampleCols}
                  getRowId={r => r.oid}
                  loading={loading}
                  selectable={!readOnly}
                  selectedIds={selected}
                  onSelectionChange={setSelected}
                  stickyColumns={stickyCount}
                  selectedRowId={activeSample}
                  onRowClick={r => setActiveSample(r.oid)}
                  onCellCommit={readOnly ? undefined : onCellCommit}
                  defaultPageSize={200}
                  initialSort={{ key: 'sampleID', dir: 'asc' }}
                  emptyText="No samples on this Pre-Login Setting."
                />
              </div>
            </div>

            <div className="sr-sl-panel sr-sl-tests">
              <PreLoginTestAssignment
                sampleOid={activeSample}
                sampleId={activeSampleId}
                tests={tests}
                bottles={bottles}
                onSaved={() => { void reloadChildren(); void loadSamples(); }}
                onEdit={() => { if (activeSample) void srHook.openTest(activeSample); }}
                onOpenContainers={key => { if (activeSample) srHook.openCont(activeSample, key); }}
                onOpenParams={key => { if (activeSample) srHook.openParams(activeSample, key); }}
                readOnly={readOnly}
              />
            </div>
          </div>
        )}

        {/* Registration's Test Assignment / Containers / parameter windows. */}
        {srHook.dialogs}

        {/* Copy Samples / Copy Test pickers. */}
        {copy === 'samples' && (
          <CopySamplesDialog
            sampleId={rows.find(r => selected.has(r.oid))?.sampleID ?? null}
            onClose={() => setCopy(null)} onOk={runCopySamples} />
        )}
        {copy === 'test' && (
          <TargetSamplesDialog
            title="Copy Test" okLabel="Copy" loading={targetsLoading} rows={targets}
            note="Please select the sample to apply the copied tests."
            emptyText="No other sample in this Pre-Login Setting shares this sample's matrix."
            onClose={() => setCopy(null)} onOk={runCopyTest} />
        )}

        {/* G5's grid context menu: Refresh · Copy To All Cell. */}
        <CellCopyMenu
          menu={cellMenu}
          onClose={() => setCellMenu(CELL_MENU_CLOSED)}
          onRefresh={() => { setCellMenu(CELL_MENU_CLOSED); void loadSamples(); }}
          onCopyAll={copyCellToSelected}
          copyAllLabel="Copy To All Cell" />

        {/* G5's "Sample Sites" popup, behind BOTH the SiteName and the
            AlternativeStation cell; it opens ticked on whichever site that cell holds. */}
        <PreLoginSitePickerDialog
          open={sitePickFor !== null}
          sites={sitePickRows}
          currentId={(() => {
            const r = rows.find(x => x.oid === sitePickFor?.oid);
            return (sitePickFor?.field === 'AlternativeStation'
              ? r?.extra?.AlternativeStationOid
              : r?.extra?.StationLocation) ?? null;
          })()}
          onPick={pickSite}
          onClose={() => setSitePickFor(null)} />

        {tab === 'containers' && (
          // Registration's Containers tab - All Containers / By Sample, editable
          // Container / Preservative / Storage, bottle picker, Assign / Copy / Reset.
          <section className="sr-sec">
            <div className="sr-sec-b" style={{ padding: 8 }}>
              {readOnly ? (
                // View only: Registration's editable ContainersTab (Assign / Copy / Reset
                // Bottles, cell editors) is not offered — a plain grid of the same rows.
                <AppDataGrid<PreLoginBottle>
                  rows={allBottles}
                  columns={bottleCols}
                  getRowId={r => r.oid}
                  defaultPageSize={20}
                  exportFilename="Pre-Login Containers"
                  emptyText="No containers on this Pre-Login Setting."
                />
              ) : (
              <PreLoginContainersPanel
                srSamples={srHook.srSamples}
                activeSample={activeSample}
                cocId={cocId}
                onPersist={srHook.persistBottles}
                onWarn={m => notify.warning(m)}
                sampleOidOf={id => rows.find(r => r.sampleID === id)?.oid}
              />
              )}
            </div>
          </section>
        )}

        {(tab === 'tests' || tab === 'parameters') && (
          <section className="sr-sec">
            <div className="sr-sec-b" style={{ padding: 8 }}>
              {/* Section toolbar — v4 §4 "Class B: child grids each own a section
                  toolbar", §6 middle altitude. Refresh + Export only: tests and
                  their containers are generated from the test library, and the
                  legacy container grid is AllowNew/AllowDelete="False". */}
              {/* These two tabs show the WHOLE COC, every sample — they are the
                  record's test and parameter collections, like the Containers tab
                  beside them, not a view of the row ticked on the Samples tab. They
                  used to be filtered to the active sample, so Tests listed one
                  sample's rows and Parameters whichever sample happened to be
                  selected last (owner report 2026-11-03). The per-sample views are
                  still right there on the Samples tab (Test Assignment, the Test /
                  Cont links), so nothing is lost. SampleID leads both grids and the
                  readers order by SampleNo, so they read sample by sample. */}
              <div className="pl-sectionbar">
                <span className="sr-sec-note">
                  {cocId ? `All samples on ${cocId}` : 'All samples'}
                </span>
                <span className="pl-sectionbar-spacer" />
                <button className="ca-btn" onClick={() => void reloadChildren()}>
                  <i className="ti ti-refresh" /> Refresh
                </button>
              </div>
              {tab === 'tests' && (
                <AppDataGrid<PreLoginTest>
                  rows={allTests}
                  columns={testCols}
                  getRowId={r => r.oid}
                  defaultPageSize={10}
                  exportFilename="Pre-Login Tests"
                  emptyText="No tests on this Pre-Login Setting."
                />
              )}
              {tab === 'parameters' && (
                <AppDataGrid<PreLoginParameter>
                  rows={allParams}
                  columns={paramCols}
                  getRowId={r => r.oid}
                  defaultPageSize={20}
                  exportFilename="Pre-Login Parameters"
                  emptyText="No parameters on this Pre-Login Setting."
                />
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
